import React, { useEffect, useState, useCallback } from "react";
import { api, formatCurrency, CURRENCIES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2, Wallet, Landmark, PiggyBank, Coins, Circle, Upload, List, GitCompare, Eraser, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import { useTranslation } from "react-i18next";
import { ImportStatementDialog, ReimportBalancesDialog, TransactionsDialog } from "@/components/AccountDialogs";
import CompareAccountsDialog from "@/components/CompareAccountsDialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const ACCOUNT_TYPES = [
  { value: "checking", label: "Cuenta corriente", icon: Landmark },
  { value: "savings", label: "Cuenta de ahorro", icon: PiggyBank },
  { value: "cash", label: "Efectivo", icon: Coins },
  { value: "other", label: "Otro", icon: Circle },
];

const EMPTY = { bank: "", alias: "", type: "checking", balance: 0, currency: "USD", notes: "" };

function AccountFormDialog({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(initial || EMPTY);
  }, [open, initial]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!form.bank?.trim() || !form.alias?.trim())
      return toast.error("Ingresa banco y alias");
    setSaving(true);
    try {
      const payload = { ...form, balance: Number(form.balance) || 0 };
      if (initial?.id) {
        await api.put(`/accounts/${initial.id}`, payload);
        toast.success("Cuenta actualizada");
      } else {
        await api.post("/accounts", payload);
        toast.success("Cuenta agregada");
      }
      onSaved?.();
      onOpenChange(false);
    } catch { toast.error("No se pudo guardar"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-white">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {initial?.id ? "Editar cuenta" : "Nueva cuenta"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          <div>
            <Label>Banco</Label>
            <Input data-testid="acc-bank" value={form.bank} onChange={(e) => set("bank", e.target.value)} placeholder="Ej: Santander" />
          </div>
          <div>
            <Label>Alias</Label>
            <Input data-testid="acc-alias" value={form.alias} onChange={(e) => set("alias", e.target.value)} placeholder="Nómina, ahorros…" />
          </div>
          <div>
            <Label>Tipo</Label>
            <Select value={form.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger data-testid="acc-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ACCOUNT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Moneda</Label>
            <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
              <SelectTrigger data-testid="acc-currency"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((c) => (
                  <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Saldo</Label>
            <Input type="number" step="any" data-testid="acc-balance"
              value={form.balance}
              onChange={(e) => set("balance", e.target.value)}
            />
          </div>
          <div className="col-span-2">
            <Label>Notas</Label>
            <Textarea data-testid="acc-notes" value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button data-testid="acc-save-btn" onClick={submit} disabled={saving} className="bg-[#0F291E] hover:bg-[#1C7C54]">
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Accounts() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importAccount, setImportAccount] = useState(null);
  const [reimportOpen, setReimportOpen] = useState(false);
  const [reimportAccount, setReimportAccount] = useState(null);
  const [txOpen, setTxOpen] = useState(false);
  const [txAccount, setTxAccount] = useState(null);
  const [txRefreshKey, setTxRefreshKey] = useState(0);
  const [compareOpen, setCompareOpen] = useState(false);
  const [dedupRunning, setDedupRunning] = useState(false);
  const [nearDupsOpen, setNearDupsOpen] = useState(false);
  const [nearDups, setNearDups] = useState(null);
  const [nearDupsLoading, setNearDupsLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const openNearDups = async () => {
    setNearDupsOpen(true);
    setNearDupsLoading(true);
    try {
      const r = await api.get(`/transactions/near-duplicates`);
      setNearDups(r.data);
      // Pre-select every row EXCEPT the oldest of each group (by imported_at).
      // That way one click on "Eliminar seleccionados" keeps the originals.
      const preselect = new Set();
      (r.data?.groups || []).forEach((g) => {
        if (!g.rows || g.rows.length < 2) return;
        const sorted = [...g.rows].sort((a, b) =>
          String(a.imported_at || "").localeCompare(String(b.imported_at || ""))
        );
        sorted.slice(1).forEach((row) => preselect.add(row.id));
      });
      setSelectedIds(preselect);
    } catch (e) {
      logger.error("near dups", e);
      toast.error("No se pudieron obtener los duplicados candidatos");
      setNearDups({ groups: [], groups_count: 0 });
      setSelectedIds(new Set());
    } finally {
      setNearDupsLoading(false);
    }
  };

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllButOldest = () => {
    const next = new Set();
    (nearDups?.groups || []).forEach((g) => {
      if (!g.rows || g.rows.length < 2) return;
      const sorted = [...g.rows].sort((a, b) =>
        String(a.imported_at || "").localeCompare(String(b.imported_at || ""))
      );
      sorted.slice(1).forEach((row) => next.add(row.id));
    });
    setSelectedIds(next);
  };

  const clearSelection = () => setSelectedIds(new Set());

  const bulkDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    try {
      const r = await api.post(`/transactions/bulk-delete`, { ids: Array.from(selectedIds) });
      const del = r.data?.deleted || 0;
      toast.success(`Eliminados ${del} movimientos`);
      setSelectedIds(new Set());
      openNearDups();
      load();
    } catch (e) {
      logger.error("bulk delete", e);
      toast.error("No se pudieron eliminar los movimientos seleccionados");
    } finally {
      setBulkDeleting(false);
    }
  };

  const deleteOneNearDup = async (row) => {
    try {
      await api.delete(`/transactions/${row.id}`);
      toast.success("Movimiento eliminado");
      // Refresh dialog contents + accounts list
      openNearDups();
      load();
    } catch (e) {
      logger.error("delete tx", e);
      toast.error("No se pudo eliminar");
    }
  };

  const runCrossDedup = async () => {
    setDedupRunning(true);
    try {
      const r = await api.post(`/transactions/deduplicate`);
      const del = r.data?.deleted || 0;
      if (del === 0) {
        toast.info("No se encontraron duplicados entre cuentas");
      } else {
        const n = r.data.accounts_touched || 0;
        toast.success(`Eliminados ${del} movimientos duplicados en ${n} cuenta${n === 1 ? "" : "s"}`);
        load();
      }
    } catch (e) {
      logger.error("cross dedup", e);
      toast.error(e?.response?.data?.detail || "No se pudo limpiar duplicados");
    } finally {
      setDedupRunning(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const res = await api.get("/accounts");
      setItems(res.data);
    } catch (e) { logger.error("accounts load", e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalByCurrency = items.reduce((acc, it) => {
    const cur = it.currency || "USD";
    acc[cur] = (acc[cur] || 0) + Number(it.balance || 0);
    return acc;
  }, {});

  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-end justify-between gap-4 fade-up">
        <div>
          <p className="uppercase text-xs tracking-[0.2em] text-[#8A9992] mb-2 flex items-center gap-2">
            <Wallet className="w-3 h-3" /> {t("accountsPage.kicker")}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-[#0A110E]">{t("accountsPage.title")}</h1>
          <p className="text-[#5F7368] mt-2">{t("accountsPage.subtitle", { defaultValue: "Efectivo y saldos disponibles." })}</p>
        </div>
        <Button data-testid="add-account-btn" onClick={() => { setEditing(null); setDialogOpen(true); }} className="bg-[#0F291E] hover:bg-[#1C7C54] rounded-xl">
          <Plus className="w-4 h-4 mr-2" /> {t("accountsPage.newAccount")}
        </Button>
      </div>

      {items.length >= 2 && (
        <div className="flex justify-end gap-2 flex-wrap">
          <button
            data-testid="near-dups-btn"
            onClick={openNearDups}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-[#EBEBEA] hover:border-[#C89A3F]/40 text-[#0A110E] bg-white"
          >
            <Eraser className="w-4 h-4 text-[#C89A3F]" /> Ver duplicados candidatos
          </button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                data-testid="cross-dedup-btn"
                disabled={dedupRunning}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-[#EBEBEA] hover:border-[#D95D39]/40 text-[#0A110E] bg-white disabled:opacity-50"
              >
                <Eraser className={`w-4 h-4 text-[#D95D39] ${dedupRunning ? "animate-pulse" : ""}`} />
                {dedupRunning ? "Limpiando…" : "Limpiar duplicados entre cuentas"}
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-white">
              <AlertDialogHeader>
                <AlertDialogTitle>¿Limpiar duplicados entre cuentas?</AlertDialogTitle>
                <AlertDialogDescription>
                  Buscará movimientos con la misma descripción, importe y moneda en ±5 días en <b>todas</b> tus cuentas.
                  Se conservará el más antiguo de cada grupo y se eliminarán los demás, incluso si están en cuentas distintas.
                  La acción no se puede deshacer.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  data-testid="cross-dedup-confirm-btn"
                  onClick={runCrossDedup}
                  className="bg-[#D95D39] hover:bg-[#D95D39]/90"
                >
                  Limpiar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <button
            data-testid="compare-accounts-btn"
            onClick={() => setCompareOpen(true)}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-[#EBEBEA] hover:border-[#1C7C54]/40 text-[#0A110E] bg-white"
          >
            <GitCompare className="w-4 h-4 text-[#1C7C54]" /> Comparar cuentas
          </button>
        </div>
      )}

      {/* totals per currency */}
      {Object.keys(totalByCurrency).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 fade-up">
          {Object.entries(totalByCurrency).map(([cur, val]) => (
            <div key={cur} className="card-surface p-5" data-testid={`total-${cur}`}>
              <div className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992]">Total {cur}</div>
              <div className="font-display text-2xl font-semibold tabular-nums mt-1 text-[#0A110E]">
                {formatCurrency(val, cur)}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="card-surface overflow-hidden">
        {loading ? (
          <div className="p-8 text-[#5F7368]">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="p-16 text-center">
            <p className="text-[#8A9992] mb-4">Aún no tienes cuentas registradas.</p>
            <Button onClick={() => { setEditing(null); setDialogOpen(true); }} className="bg-[#0F291E] hover:bg-[#1C7C54]">
              <Plus className="w-4 h-4 mr-2" /> Agregar la primera
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-[#EBEBEA]">
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992]">
              <div className="col-span-3">Banco</div>
              <div className="col-span-3">Alias</div>
              <div className="col-span-2">Tipo</div>
              <div className="col-span-2 text-right">Saldo</div>
              <div className="col-span-2"></div>
            </div>
            {items.map((it) => {
              const meta = ACCOUNT_TYPES.find((t) => t.value === it.type) || ACCOUNT_TYPES[3];
              const Icon = meta.icon;
              return (
                <div key={it.id} data-testid={`account-row-${it.id}`} className="grid grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-[#FAFAF9] transition-colors">
                  <div className="col-span-12 md:col-span-3 flex items-center gap-2">
                    <div className="w-9 h-9 rounded-lg bg-[#1C7C54]/10 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-[#1C7C54]" />
                    </div>
                    <div className="font-medium text-[#0A110E]">{it.bank}</div>
                  </div>
                  <div className="col-span-12 md:col-span-3 text-sm text-[#5F7368]">{it.alias}</div>
                  <div className="col-span-6 md:col-span-2 text-sm text-[#5F7368]">{meta.label}</div>
                  <div className="col-span-6 md:col-span-2 md:text-right text-lg font-semibold tabular-nums text-[#1C7C54] truncate">
                    {formatCurrency(it.balance, it.currency)}
                  </div>
                  <div className="col-span-12 md:col-span-2 flex justify-end gap-1 shrink-0">
                    <button
                      onClick={() => { setTxAccount(it); setTxOpen(true); }}
                      data-testid={`view-tx-${it.id}`}
                      title="Ver movimientos"
                      className="p-2 rounded-lg hover:bg-[#EBEBEA]"
                    >
                      <List className="w-4 h-4 text-[#5F7368]" />
                    </button>
                    <button
                      onClick={() => { setImportAccount(it); setImportOpen(true); }}
                      data-testid={`import-${it.id}`}
                      title="Importar extracto"
                      className="p-2 rounded-lg hover:bg-[#EBEBEA]"
                    >
                      <Upload className="w-4 h-4 text-[#1C7C54]" />
                    </button>
                    <button
                      onClick={() => { setReimportAccount(it); setReimportOpen(true); }}
                      data-testid={`reimport-${it.id}`}
                      title="Reimportar saldos"
                      className="p-2 rounded-lg hover:bg-[#EBEBEA]"
                    >
                      <RefreshCw className="w-4 h-4 text-[#5F7368]" />
                    </button>
                    <button onClick={() => { setEditing(it); setDialogOpen(true); }} data-testid={`edit-acc-${it.id}`} className="p-2 rounded-lg hover:bg-[#EBEBEA]"><Pencil className="w-4 h-4 text-[#5F7368]" /></button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button data-testid={`delete-acc-${it.id}`} className="p-2 rounded-lg hover:bg-[#EBEBEA]"><Trash2 className="w-4 h-4 text-[#D95D39]" /></button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-white">
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Eliminar cuenta?</AlertDialogTitle>
                          <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={async () => {
                              try { await api.delete(`/accounts/${it.id}`); toast.success("Eliminada"); load(); }
                              catch { toast.error("No se pudo eliminar"); }
                            }}
                            className="bg-[#D95D39] hover:bg-[#D95D39]/90"
                          >Eliminar</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <AccountFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={editing}
        onSaved={load}
      />
      <ImportStatementDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        account={importAccount}
        onImported={() => { load(); setTxRefreshKey((k) => k + 1); }}
      />
      <ReimportBalancesDialog
        open={reimportOpen}
        onOpenChange={setReimportOpen}
        account={reimportAccount}
        onImported={() => { load(); setTxRefreshKey((k) => k + 1); }}
      />
      <TransactionsDialog
        open={txOpen}
        onOpenChange={setTxOpen}
        account={txAccount}
        refreshKey={txRefreshKey}
      />
      <CompareAccountsDialog
        open={compareOpen}
        onOpenChange={setCompareOpen}
        accounts={items}
      />

      <Dialog open={nearDupsOpen} onOpenChange={setNearDupsOpen}>
        <DialogContent className="sm:max-w-3xl bg-white max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Duplicados candidatos</DialogTitle>
            <p className="text-sm text-[#5F7368]">
              Grupos con el mismo importe y moneda. Preseleccionamos todos menos el más antiguo de cada grupo — revisa y pulsa <b>Eliminar seleccionados</b>.
            </p>
          </DialogHeader>
          {nearDups?.groups?.length > 0 && (
            <div className="flex items-center justify-between text-xs text-[#5F7368] border-b border-[#EBEBEA] pb-2 gap-2 flex-wrap">
              <div>
                <span className="font-semibold tabular-nums text-[#0A110E]">{selectedIds.size}</span> movimiento{selectedIds.size === 1 ? "" : "s"} seleccionado{selectedIds.size === 1 ? "" : "s"}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="near-dup-select-all"
                  onClick={selectAllButOldest}
                  className="px-2.5 py-1 rounded-lg hover:bg-[#FAFAF9] text-[#1C7C54] underline underline-offset-2"
                >
                  Seleccionar todos menos el más antiguo
                </button>
                <button
                  type="button"
                  data-testid="near-dup-clear"
                  onClick={clearSelection}
                  className="px-2.5 py-1 rounded-lg hover:bg-[#FAFAF9] text-[#5F7368] underline underline-offset-2"
                >
                  Limpiar selección
                </button>
              </div>
            </div>
          )}
          <div className="flex-1 overflow-auto pr-1">
            {nearDupsLoading ? (
              <div className="text-sm text-[#5F7368] p-4">Cargando…</div>
            ) : !nearDups?.groups?.length ? (
              <div className="text-sm text-[#5F7368] p-4">
                No hay grupos con el mismo importe y moneda.
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {nearDups.groups.map((g) => (
                  <div
                    key={`${g.currency}-${g.amount}-${g.count}`}
                    data-testid={`near-dup-group-${g.currency}-${g.amount}`}
                    className="border border-[#EBEBEA] rounded-xl p-3"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-semibold tabular-nums">
                        {formatCurrency(g.amount, g.currency)}
                      </div>
                      <div className="text-xs text-[#8A9992]">{g.count} movimientos</div>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      {g.rows.map((r) => {
                        const acc = items.find((a) => a.id === r.account_id);
                        const isChecked = selectedIds.has(r.id);
                        return (
                          <label
                            key={r.id}
                            data-testid={`near-dup-row-${r.id}`}
                            className={`flex items-center gap-2 text-xs rounded-lg px-2.5 py-1.5 cursor-pointer transition-colors ${isChecked ? "bg-[#D95D39]/8 border border-[#D95D39]/30" : "bg-[#FAFAF9] hover:bg-[#F0EFEC]"}`}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => toggleSelected(r.id)}
                              data-testid={`near-dup-check-${r.id}`}
                              className="w-4 h-4 accent-[#D95D39]"
                            />
                            <div className="text-[#5F7368] tabular-nums w-24 shrink-0">{r.date}</div>
                            <div className="flex-1 min-w-0">
                              <div className="text-[#0A110E] truncate" title={r.description}>{r.description}</div>
                              <div className="text-[10px] text-[#8A9992] truncate">
                                {acc ? `${acc.bank} · ${acc.alias}` : r.account_id} · key: <span className="font-mono">{r.dedup_key}</span>
                              </div>
                            </div>
                            <button
                              type="button"
                              data-testid={`near-dup-delete-${r.id}`}
                              onClick={(e) => { e.preventDefault(); deleteOneNearDup(r); }}
                              title="Eliminar este movimiento"
                              className="p-1.5 rounded-lg hover:bg-[#D95D39]/10 text-[#D95D39]"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setNearDupsOpen(false)}>Cerrar</Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  data-testid="near-dup-bulk-delete-btn"
                  disabled={selectedIds.size === 0 || bulkDeleting}
                  className="bg-[#D95D39] hover:bg-[#D95D39]/90 text-white"
                >
                  <Trash2 className="w-4 h-4 mr-1.5" />
                  {bulkDeleting ? "Eliminando…" : `Eliminar seleccionados (${selectedIds.size})`}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-white">
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Eliminar {selectedIds.size} movimiento{selectedIds.size === 1 ? "" : "s"}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Se borrarán de forma permanente. La acción no se puede deshacer.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    data-testid="near-dup-bulk-confirm"
                    onClick={bulkDeleteSelected}
                    className="bg-[#D95D39] hover:bg-[#D95D39]/90"
                  >
                    Eliminar
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
